import { NOT_ADVERBS, countWords, type ParagraphInfo, type ProseAnalysis, type SentenceInfo } from './proseAnalysis'
import { NOT_NAMES, collectCharacterNames, splitQuotes } from './screenplay'
import {
  BEAT_MIN_WORDS,
  ECHO_IGNORE,
  ECHO_MIN_LETTERS,
  ECHO_MIN_RANK,
  ECHO_TIERS,
  FILTER_HELPERS,
  FILTER_SUBJECTS,
  FILTER_VERBS,
  FILTER_VERBS_AFTER_HELPER,
  NEUTRAL_TAGS,
  PARAGRAPH_WALL_WORDS,
  SAID_BOOKISMS,
  UNTAGGED_RUN_LENGTH,
} from './editorModeTuning'

// Editor Mode's habit lenses: word echoes, filter words, crutch words,
// dialogue tags and paragraph walls. Each finds marks in the measured page;
// the lens extension draws them and the panel counts them. The detectors
// favor precision: a missed habit costs little, a wrong mark costs trust.
// Every list and threshold lives in editorModeTuning.ts.

export type HabitLens = 'echoes' | 'filterWords' | 'crutchWords' | 'dialogueTags' | 'paragraphWalls'
export type HabitKind = 'echo' | 'filter' | 'crutch' | 'bookism' | 'tagAdverb' | 'untagged' | 'wall'

export const HABIT_KIND_LABELS: Record<HabitKind, string> = {
  echo: 'Word echoes',
  filter: 'Filter words',
  crutch: 'Crutch words',
  bookism: 'Said-bookisms',
  tagAdverb: 'Adverbs on tags',
  untagged: 'Untagged runs',
  wall: 'Paragraph walls',
}

/** A habit mark as a loose-end note: what the lens saw. */
export function describeHabit(kind: HabitKind, word: string, count = 1): string {
  switch (kind) {
    case 'echo': return `Echo: “${word}”${count > 1 ? ` ×${count}` : ''}`
    case 'filter': return `Filter word: ${word}`
    case 'crutch': return `Crutch word: ${word}`
    case 'bookism': return `Said-bookism: ${word}`
    case 'tagAdverb': return `Adverb on tag: ${word}`
    case 'untagged': return `${count} untagged lines: who is speaking?`
    case 'wall': return `Paragraph wall: ${word} words`
  }
}

export interface HabitMark {
  kind: HabitKind
  /** Document positions. For untagged and wall marks, the paragraph's text. */
  from: number
  to: number
  /** The word or phrase, lower-cased; what "Never flag" adds and the panel groups by. */
  word: string
  /** Stable across sessions while the sentence is unchanged; what "Ignore here" stores. */
  ignoreKey: string
  /** Marks that belong together: one echoed word, or one untagged run. */
  group?: string
  /** Whole-paragraph marks. */
  block?: boolean
}

export interface HabitOptions {
  lenses: Record<HabitLens, boolean>
  crutchList: string[]
  crutchLyAdverbs: boolean
  /** Lower-cased words and phrases never flagged. */
  neverFlag: Set<string>
  /** Ignore keys dismissed on this page. */
  ignores: Set<string>
  /** Whether a lens also looks inside quotations. */
  inDialogue: { echoes: boolean; filterWords: boolean; crutchWords: boolean }
  /** Word → frequency rank; echoes wait until it has loaded. */
  ranks: Map<string, number> | null
  /** Lower-cased words of names from the Reference Manager (page titles and aliases). */
  knownNames?: Set<string>
}

export interface HabitAnalysis {
  marks: HabitMark[]
  /** Words on the page, for rates. */
  words: number
}

// ── Small helpers ────────────────────────────────────────────────────────

export function normalizeHabitWord(word: string): string {
  return word.toLowerCase().replace(/’/g, "'").replace(/\s+/g, ' ').trim()
}

/** A short, stable hash for ignore keys. */
export function shortHash(text: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return (h >>> 0).toString(36)
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/** A list entry as a pattern: spaces match any run of spaces, apostrophes either kind. */
function phrasePattern(entry: string): string {
  return escapeRegExp(normalizeHabitWord(entry)).replace(/ /g, '\\s+').replace(/'/g, "['’]")
}

function alternation(entries: string[]): string {
  return [...new Set(entries.map(normalizeHabitWord).filter(Boolean))]
    .sort((a, b) => b.length - a.length)
    .map(phrasePattern)
    .join('|')
}

const B = "(?<![\\p{L}\\p{N}'’])"
const E = "(?![\\p{L}\\p{N}'’])"

/**
 * A light root for echoes, so walk / walks / walked / walking match while
 * unrelated words (university / universe) stay apart.
 */
export function echoStem(word: string): string {
  let w = normalizeHabitWord(word).replace(/'s$/, '')
  if (w.length >= 5 && /ies$/.test(w)) w = w.slice(0, -3) + 'y'
  else if (w.length >= 5 && /ied$/.test(w)) w = w.slice(0, -3) + 'y'
  else if (w.length >= 6 && w.endsWith('ing')) w = w.slice(0, -3)
  else if (w.length >= 5 && w.endsWith('ed')) w = w.slice(0, -2)
  else if (w.length >= 6 && w.endsWith('ly')) w = w.slice(0, -2)
  else if (w.length >= 5 && /(?:ch|sh|x|ss|z)es$/.test(w)) w = w.slice(0, -2)
  else if (w.length >= 4 && w.endsWith('s') && !/(?:ss|us|is)$/.test(w)) w = w.slice(0, -1)
  if (/([bdfgklmnprtz])\1$/.test(w)) w = w.slice(0, -1)
  if (w.length > 3 && w.endsWith('e')) w = w.slice(0, -1)
  return w
}

export function echoDistance(rank: number): number {
  for (const tier of ECHO_TIERS) if (rank <= tier.maxRank) return tier.distance
  return Infinity
}

function isLyAdverb(word: string): boolean {
  const w = normalizeHabitWord(word)
  return w.length > 4 && w.endsWith('ly') && !NOT_ADVERBS.has(w)
}

/** Quoted character ranges of a paragraph's text. */
function quotedRanges(text: string): { start: number; end: number }[] {
  return splitQuotes(text).segments
    .filter((s) => s.kind === 'dialogue')
    .map((s) => ({ start: s.start, end: s.start + s.text.length }))
}

function insideAny(ranges: { start: number; end: number }[], index: number): boolean {
  return ranges.some((r) => index >= r.start && index < r.end)
}

/** The sentence holding a position; sentences are in document order. */
function sentenceAt(sentences: SentenceInfo[], pos: number): SentenceInfo | null {
  let lo = 0
  let hi = sentences.length - 1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    const s = sentences[mid]
    if (pos < s.from) hi = mid - 1
    else if (pos >= s.to) lo = mid + 1
    else return s
  }
  return null
}

function contextKey(kind: HabitKind, word: string, context: string): string {
  return `${kind}:${word}:${shortHash(context)}`
}

// ── Detectors ────────────────────────────────────────────────────────────

const WORD_RE = /[\p{L}\p{N}]+(?:['’\-][\p{L}\p{N}]+)*/gu

function findEchoes(analysis: ProseAnalysis, options: HabitOptions, out: HabitMark[]) {
  const ranks = options.ranks
  if (!ranks) return
  // Names repeat as a matter of course, even where they start a sentence.
  const names = new Set([...collectCharacterNames(analysis.paragraphs)].flatMap((n) => n.split(/\s+/)).map(normalizeHabitWord))
  const ignoreList = new Set(ECHO_IGNORE.map(normalizeHabitWord))
  interface Seen { index: number; mark: HabitMark; group: string | null }
  const lastSeen = new Map<string, Seen>()
  const pending: HabitMark[] = []
  let wordIndex = 0
  let groupCount = 0

  for (const paragraph of analysis.paragraphs) {
    if (paragraph.kind !== 'paragraph') {
      // A scene break starts the reader's memory afresh.
      if (paragraph.kind === 'rule' || paragraph.kind === 'heading') lastSeen.clear()
      continue
    }
    const quotes = options.inDialogue.echoes ? [] : quotedRanges(paragraph.text)
    for (const match of paragraph.text.matchAll(WORD_RE)) {
      const index = wordIndex++
      const raw = match[0]
      const at = match.index ?? 0
      if (quotes.length && insideAny(quotes, at)) continue
      const word = normalizeHabitWord(raw).replace(/'s$/, '')
      if ((word.match(/\p{L}/gu)?.length ?? 0) < ECHO_MIN_LETTERS) continue
      const from = paragraph.from + at
      const sentence = sentenceAt(analysis.sentences, from)
      // A capital away from the start of a sentence is a name.
      if (/^\p{Lu}/u.test(raw) && (!sentence || sentence.openerFrom !== from)) continue
      if (names.has(word)) continue
      // A known name, capitalized, even where it starts a sentence.
      if (options.knownNames?.has(word) && /^\p{Lu}/u.test(raw)) continue
      const rank = ranks.get(word) ?? Infinity
      if (rank < ECHO_MIN_RANK) continue
      const stem = echoStem(word)
      if (ignoreList.has(word) || options.neverFlag.has(word) || options.neverFlag.has(stem)) continue
      const mark: HabitMark = {
        kind: 'echo',
        from,
        to: from + raw.length,
        word: stem,
        ignoreKey: contextKey('echo', stem, sentence?.text ?? paragraph.text),
      }
      if (options.ignores.has(mark.ignoreKey)) continue
      const previous = lastSeen.get(stem)
      if (previous && index - previous.index <= echoDistance(rank)) {
        let group = previous.group
        if (!group) {
          group = `echo-${groupCount++}`
          previous.mark.group = group
          pending.push(previous.mark)
        }
        mark.group = group
        pending.push(mark)
        lastSeen.set(stem, { index, mark, group })
      } else {
        lastSeen.set(stem, { index, mark, group: null })
      }
    }
  }
  out.push(...pending)
}

function findFilterWords(analysis: ProseAnalysis, options: HabitOptions, out: HabitMark[]) {
  const subjects = alternation(FILTER_SUBJECTS)
  const helpers = alternation(FILTER_HELPERS)
  const verbs = alternation(FILTER_VERBS)
  const afterHelper = alternation(FILTER_VERBS_AFTER_HELPER)
  // Pronouns in any case; a name is any capitalized word (checked below), so
  // that pattern must stay case-sensitive.
  const pronoun = `${B}((?:${subjects})(?:['’](?:d|ll|ve|s))?)`
  const name = `${B}(\\p{Lu}[\\p{Ll}'’\\-]+(?:['’](?:d|ll|ve|s))?)`
  const between = `((?:\\s+(?:${helpers}|\\p{L}{3,}ly)){0,2})`
  const patterns = [
    new RegExp(`${pronoun}${between}(\\s+)(${verbs})${E}`, 'giu'),
    new RegExp(`${pronoun}((?:\\s+(?:${helpers})){1,2})(\\s+)(${afterHelper})${E}`, 'giu'),
    new RegExp(`${name}${between}(\\s+)(${verbs})${E}`, 'gu'),
    new RegExp(`${name}((?:\\s+(?:${helpers})){1,2})(\\s+)(${afterHelper})${E}`, 'gu'),
  ]

  for (const paragraph of analysis.paragraphs) {
    if (paragraph.kind !== 'paragraph' || !paragraph.text.trim()) continue
    const quotes = options.inDialogue.filterWords ? [] : quotedRanges(paragraph.text)
    const seen = new Set<number>()
    for (const re of patterns) {
      for (const match of paragraph.text.matchAll(re)) {
        const [, subject, mid, gap, verb] = match
        const bare = subject.replace(/['’]\w+$/, '')
        // A capitalized subject must be a name: not a common capitalized word
        // ("Then"), unless the Reference Manager knows it as one ("May").
        if (!re.flags.includes('i') && !options.knownNames?.has(bare.toLowerCase()) && (NOT_NAMES.has(bare) || isLyAdverb(bare))) continue
        const at = match.index ?? 0
        const verbAt = at + subject.length + mid.length + gap.length
        if (seen.has(verbAt) || (quotes.length && insideAny(quotes, verbAt))) continue
        // "It" only filters with seeming: "it seemed", not "it noticed".
        if (subject.toLowerCase() === 'it' && !/^(?:seem|appear)/i.test(verb)) continue
        const word = normalizeHabitWord(verb)
        if (options.neverFlag.has(word)) continue
        seen.add(verbAt)
        const markFrom = paragraph.from + (mid.trim() ? at + subject.length + (mid.length - mid.trimStart().length) : verbAt)
        const from = paragraph.from + verbAt
        const sentence = sentenceAt(analysis.sentences, from)
        const mark: HabitMark = {
          kind: 'filter',
          from: markFrom,
          to: from + verb.length,
          word,
          ignoreKey: contextKey('filter', word, sentence?.text ?? paragraph.text),
        }
        if (!options.ignores.has(mark.ignoreKey)) out.push(mark)
      }
    }
  }
}

function findCrutchWords(analysis: ProseAnalysis, options: HabitOptions, out: HabitMark[]) {
  const list = options.crutchList.filter((w) => !options.neverFlag.has(normalizeHabitWord(w)))
  const listed = list.length ? new RegExp(`${B}(${alternation(list)})${E}`, 'giu') : null
  const adverbs = options.crutchLyAdverbs ? /(?<![\p{L}\p{N}'’])(\p{L}{3,}ly)(?![\p{L}\p{N}'’])/gu : null
  if (!listed && !adverbs) return

  for (const paragraph of analysis.paragraphs) {
    if (paragraph.kind !== 'paragraph' || !paragraph.text.trim()) continue
    const quotes = options.inDialogue.crutchWords ? [] : quotedRanges(paragraph.text)
    const taken = new Set<number>()
    for (const re of [listed, adverbs]) {
      if (!re) continue
      for (const match of paragraph.text.matchAll(re)) {
        const at = match.index ?? 0
        if (taken.has(at) || (quotes.length && insideAny(quotes, at))) continue
        const word = normalizeHabitWord(match[1])
        if (re === adverbs && (!isLyAdverb(word) || options.neverFlag.has(word))) continue
        taken.add(at)
        const from = paragraph.from + at
        const sentence = sentenceAt(analysis.sentences, from)
        const mark: HabitMark = {
          kind: 'crutch',
          from,
          to: from + match[1].length,
          word,
          ignoreKey: contextKey('crutch', word, sentence?.text ?? paragraph.text),
        }
        if (!options.ignores.has(mark.ignoreKey)) out.push(mark)
      }
    }
  }
}

const TAG_SUBJECT = "(?:I|[Hh]e|[Ss]he|[Tt]hey|[Ww]e|[Yy]ou|\\p{Lu}[\\p{L}'’\\-]+(?:\\s+\\p{Lu}[\\p{L}'’\\-]+)?|(?:[Tt]he|[Aa]n?|[Hh]is|[Hh]er|[Tt]heir|[Mm]y|[Oo]ur)\\s+\\p{Ll}+(?:\\s+\\p{Ll}+)?)"
const ADVERB = '(\\p{L}{3,}ly)'

interface TagHit {
  verb: string
  verbAt: number
  adverb: string | null
  adverbAt: number
}

function tagPatterns() {
  const verbs = `(${alternation([...SAID_BOOKISMS, ...NEUTRAL_TAGS])})`
  return {
    // ”, she said softly / ”, she softly said
    subjectFirst: new RegExp(`^([\\s,]*${TAG_SUBJECT}\\s+)(?:${ADVERB}(\\s+))?${verbs}(?:(\\s+)${ADVERB})?${E}`, 'u'),
    // ”, said Mara softly
    verbFirst: new RegExp(`^([\\s,]*)${verbs}(\\s+${TAG_SUBJECT})(?:(\\s+)${ADVERB})?${E}`, 'u'),
    // She said softly, “
    before: new RegExp(`${B}(${TAG_SUBJECT}\\s+)(?:${ADVERB}(\\s+))?${verbs}(?:(\\s+)${ADVERB})?([\\s,:]*)$`, 'u'),
  }
}

function matchTag(text: string, patterns: ReturnType<typeof tagPatterns>, side: 'after' | 'before'): TagHit | null {
  if (side === 'after') {
    let m = text.match(patterns.subjectFirst)
    if (m) {
      const [, lead, adverbBefore, gapBefore, verb, gapAfter, adverbAfter] = m
      const verbAt = lead.length + (adverbBefore ? adverbBefore.length + gapBefore.length : 0)
      if (adverbBefore) return { verb, verbAt, adverb: adverbBefore, adverbAt: lead.length }
      return { verb, verbAt, adverb: adverbAfter ?? null, adverbAt: verbAt + verb.length + (gapAfter?.length ?? 0) }
    }
    m = text.match(patterns.verbFirst)
    if (m) {
      const [, lead, verb, subject, gap, adverb] = m
      return { verb, verbAt: lead.length, adverb: adverb ?? null, adverbAt: lead.length + verb.length + subject.length + (gap?.length ?? 0) }
    }
    return null
  }
  const m = text.match(patterns.before)
  if (!m) return null
  const [, subject, adverbBefore, gapBefore, verb, gapAfter, adverbAfter] = m
  const at = m.index ?? 0
  const verbAt = at + subject.length + (adverbBefore ? adverbBefore.length + gapBefore.length : 0)
  if (adverbBefore) return { verb, verbAt, adverb: adverbBefore, adverbAt: at + subject.length }
  return { verb, verbAt, adverb: adverbAfter ?? null, adverbAt: verbAt + verb.length + (gapAfter?.length ?? 0) }
}

function findDialogueTags(analysis: ProseAnalysis, options: HabitOptions, out: HabitMark[]) {
  const patterns = tagPatterns()
  const bookisms = new Set(SAID_BOOKISMS.map(normalizeHabitWord))
  let run: ParagraphInfo[] = []
  let runCount = 0

  const closeRun = () => {
    if (run.length >= UNTAGGED_RUN_LENGTH) {
      const group = `untagged-${runCount++}`
      const ignoreKey = contextKey('untagged', '', run[0].text)
      if (!options.ignores.has(ignoreKey)) {
        for (const paragraph of run) {
          out.push({ kind: 'untagged', from: paragraph.from, to: paragraph.to, word: '', ignoreKey, group, block: true })
        }
      }
    }
    run = []
  }

  for (const paragraph of analysis.paragraphs) {
    if (paragraph.kind !== 'paragraph') {
      closeRun()
      continue
    }
    if (!paragraph.text.trim()) continue
    const { segments } = splitQuotes(paragraph.text)
    if (!segments.some((s) => s.kind === 'dialogue')) {
      closeRun()
      continue
    }

    const narrationWords = segments.filter((s) => s.kind === 'narration').reduce((n, s) => n + countWords(s.text), 0)
    if (narrationWords < BEAT_MIN_WORDS) run.push(paragraph)
    else closeRun()

    segments.forEach((segment, i) => {
      if (segment.kind !== 'narration') return
      const hits: TagHit[] = []
      if (segments[i - 1]?.kind === 'dialogue') {
        // The closing quotation mark sits between the segments.
        const hit = matchTag(segment.text, patterns, 'after')
        if (hit) hits.push(hit)
      }
      if (segments[i + 1]?.kind === 'dialogue') {
        const hit = matchTag(segment.text, patterns, 'before')
        if (hit && !hits.some((h) => h.verbAt === hit.verbAt)) hits.push(hit)
      }
      for (const hit of hits) {
        const base = paragraph.from + segment.start
        const verb = normalizeHabitWord(hit.verb)
        const sentence = sentenceAt(analysis.sentences, base + hit.verbAt)
        const context = sentence?.text ?? paragraph.text
        if (bookisms.has(verb) && !options.neverFlag.has(verb)) {
          const mark: HabitMark = {
            kind: 'bookism',
            from: base + hit.verbAt,
            to: base + hit.verbAt + hit.verb.length,
            word: verb,
            ignoreKey: contextKey('bookism', verb, context),
          }
          if (!options.ignores.has(mark.ignoreKey)) out.push(mark)
        }
        if (hit.adverb && isLyAdverb(hit.adverb) && !options.neverFlag.has(normalizeHabitWord(hit.adverb))) {
          const word = normalizeHabitWord(hit.adverb)
          const mark: HabitMark = {
            kind: 'tagAdverb',
            from: base + hit.adverbAt,
            to: base + hit.adverbAt + hit.adverb.length,
            word,
            ignoreKey: contextKey('tagAdverb', word, context),
          }
          if (!options.ignores.has(mark.ignoreKey)) out.push(mark)
        }
      }
    })
  }
  closeRun()
}

function findWalls(analysis: ProseAnalysis, options: HabitOptions, out: HabitMark[]) {
  for (const paragraph of analysis.paragraphs) {
    if (paragraph.kind !== 'paragraph') continue
    const words = paragraph.sentences.reduce((n, s) => n + s.words, 0)
    if (words < PARAGRAPH_WALL_WORDS) continue
    const mark: HabitMark = {
      kind: 'wall',
      from: paragraph.from,
      to: paragraph.to,
      word: String(words),
      ignoreKey: contextKey('wall', '', paragraph.text),
      block: true,
    }
    if (!options.ignores.has(mark.ignoreKey)) out.push(mark)
  }
}

/** Every habit mark on the page for the lenses that are on, in document order. */
export function analyzeHabits(analysis: ProseAnalysis, options: HabitOptions): HabitAnalysis {
  const marks: HabitMark[] = []
  const { lenses } = options
  if (lenses.echoes) findEchoes(analysis, options, marks)
  if (lenses.filterWords) findFilterWords(analysis, options, marks)
  if (lenses.crutchWords) findCrutchWords(analysis, options, marks)
  if (lenses.dialogueTags) findDialogueTags(analysis, options, marks)
  if (lenses.paragraphWalls) findWalls(analysis, options, marks)
  marks.sort((a, b) => a.from - b.from || a.to - b.to)
  return { marks, words: analysis.sentences.reduce((n, s) => n + s.words, 0) }
}

/** Marks counted by kind, and within each kind by word, most frequent first. */
export function summarizeHabits(marks: HabitMark[]): Map<HabitKind, { count: number; words: { word: string; count: number }[] }> {
  const summary = new Map<HabitKind, { count: number; byWord: Map<string, number>; groups: Set<string> }>()
  for (const mark of marks) {
    let entry = summary.get(mark.kind)
    if (!entry) summary.set(mark.kind, (entry = { count: 0, byWord: new Map(), groups: new Set() }))
    // Echoes and untagged runs count once per group, not per mark.
    if (mark.group) {
      if (entry.groups.has(mark.group)) continue
      entry.groups.add(mark.group)
    }
    entry.count++
    if (mark.word) entry.byWord.set(mark.word, (entry.byWord.get(mark.word) ?? 0) + 1)
  }
  const result = new Map<HabitKind, { count: number; words: { word: string; count: number }[] }>()
  for (const [kind, entry] of summary) {
    result.set(kind, {
      count: entry.count,
      words: [...entry.byWord.entries()].map(([word, count]) => ({ word, count })).sort((a, b) => b.count - a.count || a.word.localeCompare(b.word)),
    })
  }
  return result
}
